import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  apiFetch, aiGenerateTemplate,
  getTemplates, createTemplate, updateTemplate, publishTemplate,
  archiveTemplate, deleteTemplate, duplicateTemplate,
  getEmailLogs, getEmailLog, getEmailStats, sendTemplateTestEmail,
} from '../services/api';
import PageHeader from '../components/common/PageHeader';
/* G94 — re-skin chrome with v2. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';
import {
  Plus, Search, Filter, FileText, Mail, MessageSquare, Workflow, Copy,
  Trash2, Edit3, Eye, Send, CheckCircle, XCircle, Clock, Archive,
  ChevronDown, ChevronRight, BarChart3, RefreshCw, Zap, Sparkles,
  Bot, User, X, Maximize2, Minimize2, ArrowRight, Wand2, LayoutTemplate,
} from 'lucide-react';

/* ─── Brand & Style Constants ─── */
const BRAND = { primary: '#0e3552', dark: '#0a1d44', light: '#eef2f8' };
const stone = { 50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 900: '#1c1917' };
const btnBase = { border: 'none', cursor: 'pointer', borderRadius: 6, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 };
const inputStyle = { width: '100%', padding: '8px 12px', border: `1px solid ${stone[200]}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', color: stone[900] };

const TEMPLATE_TYPES = [
  { id: 'email', label: 'Email Templates', icon: Mail, color: '#2563eb', desc: 'Email notifications, alerts, and reports' },
  { id: 'prompt', label: 'Prompt Templates', icon: MessageSquare, color: '#7c3aed', desc: 'AI prompts for agents and workflows' },
  { id: 'workflow', label: 'Workflow Templates', icon: Workflow, color: '#059669', desc: 'Reusable workflow snippets and patterns' },
  { id: 'response', label: 'Response Templates', icon: FileText, color: '#d97706', desc: 'Canned responses for incident comms' },
];

const CATEGORIES = {
  email: ['Incident Alert', 'Escalation', 'Status Update', 'Report', 'Notification', 'General'],
  prompt: ['Analysis', 'Summarization', 'Classification', 'Response Generation', 'Data Extraction', 'General'],
  workflow: ['Incident Response', 'Threat Hunting', 'Compliance', 'Remediation', 'Identity & Access', 'General'],
  response: ['Incident Update', 'Stakeholder Communication', 'Post-Mortem', 'SLA Breach', 'General'],
};

/* ─── AI system prompts per template type ─── */
const AI_SYSTEM_PROMPTS = {
  email: `You are an expert email template builder for the Prove7 Governed Agent Orchestration platform. You create professional HTML email templates for security operations.

Rules:
- Use the Prove7 brand: primary #0e3552, dark #0a1d44, background gradient 135deg from #0e3552 to #0a1d44
- Font family: Inter, system-ui, sans-serif
- Max width 600px, border-radius 12px, clean professional look
- Include a gradient header bar with title and subtitle
- Use \${variable_name} syntax for dynamic values
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "subject": "...", "body": "<html>...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- The body MUST be valid HTML suitable for email clients
- Always include a footer with the Prove7 branding
- Choose category from: Incident Alert, Escalation, Status Update, Report, Notification, General`,

  prompt: `You are an expert AI prompt engineer for the Prove7 Governed Agent Orchestration platform. You create system prompts and prompt templates for security operations AI tasks.

Rules:
- Create clear, structured prompts with explicit instructions
- Use \${variable_name} syntax for dynamic input injection
- Include output format specifications (JSON, markdown, etc.)
- Define the AI persona and expertise area
- Include guardrails and constraints
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "The prompt text...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Analysis, Summarization, Classification, Response Generation, Data Extraction, General`,

  workflow: `You are an expert workflow architect for the Prove7 platform. You design workflow template specifications that describe reusable security automation patterns.

Rules:
- Describe workflows as JSON metadata objects (not full DAG definitions)
- Include: playbookId, category, nodes list, pattern description, connectorRequirements, estimatedDuration
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "{\\"playbookId\\":\\"...\\", ...}", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Incident Response, Threat Hunting, Compliance, Remediation, Identity & Access, General
- Think about the full orchestration: trigger, enrichment, decision gates, remediation, notification`,

  response: `You are an expert incident communication writer for the Prove7 Governed Agent Orchestration platform. You create structured response templates for security operations communications.

Rules:
- Use Markdown formatting for structure
- Include placeholders with \${variable_name} syntax
- Structure: header, situation/summary, impact, actions, next steps, footer
- Keep language professional but clear and actionable
- Return a JSON object with these exact fields:
  {"name": "...", "description": "...", "body": "The markdown response template...", "category": "...", "tags": "comma,separated", "variables": "[\\"var1\\",\\"var2\\"]"}
- Choose category from: Incident Update, Stakeholder Communication, Post-Mortem, SLA Breach, General`,
};

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, color = stone[900] }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${stone[200]}`, borderRadius: 8, padding: '16px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {Icon && <Icon size={16} color={color} />}
        <span style={{ fontSize: 11, color: stone[500], fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ─── Badge ─── */
function Badge({ text, color = stone[500], bg = stone[100] }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color, background: bg, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* AI TEMPLATE BUILDER CHAT                                                   */
/* ═══════════════════════════════════════════════════════════════════════════ */
function AITemplateChatPanel({ templateType, onInsertTemplate, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const typeInfo = TEMPLATE_TYPES.find(t => t.id === templateType);
  const typeLabel = typeInfo?.label?.replace('Templates', '').trim() || 'Template';

  const SUGGESTIONS = {
    email: [
      'Create an incident alert email with severity badge and action button',
      'Build a weekly security digest email with summary stats',
      'Make an account lockout notification email',
      'Design a credential expiry warning email',
    ],
    prompt: [
      'Create a threat classification prompt that outputs severity and IOCs',
      'Build a log analysis prompt that extracts anomalies',
      'Make a vulnerability assessment prompt with CVSS scoring',
      'Design a phishing email analysis prompt',
    ],
    workflow: [
      'Design a ransomware detection and response playbook',
      'Create a DLP alert triage and remediation workflow',
      'Build an account compromise investigation playbook',
      'Make a vulnerability patch management workflow',
    ],
    response: [
      'Create a major incident stakeholder update template',
      'Build a security advisory communication template',
      'Make a compliance audit findings report template',
      'Design a breach notification response template',
    ],
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Welcome message
    setMessages([{
      role: 'assistant',
      content: `I'm your AI ${typeLabel} Template Builder. Describe what kind of ${typeLabel.toLowerCase()} template you need and I'll generate it for you.\n\nI'll create a complete template with proper structure, variables, and formatting that you can save directly to the Template Center.`,
      timestamp: new Date(),
    }]);
  }, [templateType]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const systemPrompt = AI_SYSTEM_PROMPTS[templateType];
      const fullPrompt = `${systemPrompt}\n\nUser request: ${text}\n\nReturn ONLY the JSON object, no markdown fences, no explanation.`;
      const res = await aiGenerateTemplate(fullPrompt, templateType);

      if (res?.ok && res.data) {
        let parsed = null;
        let rawText = '';

        // The dedicated template AI endpoint returns { ok, response, model, provider }
        const responseText = typeof res.data === 'string' ? res.data
          : res.data?.response ? (typeof res.data.response === 'string' ? res.data.response : JSON.stringify(res.data.response))
          : JSON.stringify(res.data);

        rawText = responseText;

        // Try to parse JSON from response
        try {
          // Strip markdown code fences if present
          const cleaned = rawText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
          parsed = JSON.parse(cleaned);
        } catch {
          // Try to find JSON in the response
          const jsonMatch = rawText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            try { parsed = JSON.parse(jsonMatch[0]); } catch { /* ignore */ }
          }
        }

        if (parsed && parsed.name && parsed.body) {
          const assistantMsg = {
            role: 'assistant',
            content: `I've created a **${parsed.name}** template for you.\n\n**Category:** ${parsed.category || 'General'}\n**Description:** ${parsed.description || ''}\n${parsed.subject ? `**Subject:** ${parsed.subject}\n` : ''}**Variables:** ${parsed.variables || '[]'}\n**Tags:** ${parsed.tags || ''}\n\nYou can preview the content below and click "Use This Template" to add it to your Template Center.`,
            timestamp: new Date(),
            templateData: parsed,
          };
          setMessages(prev => [...prev, assistantMsg]);
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `I generated a response but couldn't parse it into a proper template structure. Here's what I got:\n\n${rawText.substring(0, 500)}...\n\nCould you try rephrasing your request? Be specific about the template purpose, variables needed, and desired format.`,
            timestamp: new Date(),
          }]);
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I encountered an error generating the template: ${res?.error || res?.data?.error || 'Unknown error'}. Please check that an API key is configured in **Inference Controls** and try again.`,
          timestamp: new Date(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Something went wrong: ${err.message}. Please try again.`,
        timestamp: new Date(),
      }]);
    }
    setLoading(false);
  };

  const handleUseTemplate = (templateData) => {
    onInsertTemplate({
      ...templateData,
      templateType,
      status: 'draft',
    });
  };

  const panelWidth = expanded ? '50%' : 380;

  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: panelWidth,
      background: '#fff', borderLeft: `1px solid ${stone[200]}`,
      boxShadow: '-4px 0 24px rgba(0,0,0,0.08)', zIndex: 900,
      display: 'flex', flexDirection: 'column', transition: 'width 0.2s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${stone[200]}`,
        background: `linear-gradient(135deg, ${typeInfo?.color}08, ${typeInfo?.color}03)`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: typeInfo?.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={14} color={typeInfo?.color} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: stone[900] }}>AI {typeLabel} Builder</div>
            <div style={{ fontSize: 10, color: stone[500] }}>Describe your template and I'll create it</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setExpanded(!expanded)}
            style={{ ...btnBase, padding: 6, background: 'transparent', color: stone[400] }}>
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button onClick={onClose}
            style={{ ...btnBase, padding: 6, background: 'transparent', color: stone[400] }}>
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: msg.role === 'user' ? BRAND.primary : typeInfo?.color + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {msg.role === 'user' ? <User size={12} color="#fff" /> : <Bot size={12} color={typeInfo?.color} />}
            </div>
            <div style={{
              maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
              background: msg.role === 'user' ? BRAND.primary : stone[50],
              color: msg.role === 'user' ? '#fff' : stone[900],
              fontSize: 12, lineHeight: 1.6,
              borderTopRightRadius: msg.role === 'user' ? 4 : 12,
              borderTopLeftRadius: msg.role === 'user' ? 12 : 4,
            }}>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {msg.content.split(/(\*\*.*?\*\*)/).map((part, j) =>
                  part.startsWith('**') && part.endsWith('**')
                    ? <strong key={j}>{part.slice(2, -2)}</strong>
                    : <span key={j}>{part}</span>
                )}
              </div>

              {/* Template preview & use button */}
              {msg.templateData && (
                <div style={{ marginTop: 10, borderTop: `1px solid ${msg.role === 'user' ? 'rgba(255,255,255,0.2)' : stone[200]}`, paddingTop: 10 }}>
                  {/* Preview label */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: stone[500], textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.5px' }}>
                    Preview
                  </div>
                  {/* Preview body — use iframe for HTML emails, monospace for others */}
                  {templateType === 'email' && msg.templateData.body ? (
                    <iframe
                      title="Email Preview"
                      srcDoc={msg.templateData.body}
                      sandbox=""
                      style={{
                        width: '100%', height: 200, border: `1px solid ${stone[200]}`,
                        borderRadius: 6, background: '#fff', marginBottom: 8,
                      }}
                    />
                  ) : (
                    <div style={{
                      padding: 10, borderRadius: 6, fontSize: 11, fontFamily: 'monospace',
                      background: msg.role === 'user' ? 'rgba(255,255,255,0.1)' : '#fff',
                      border: `1px solid ${msg.role === 'user' ? 'rgba(255,255,255,0.15)' : stone[200]}`,
                      maxHeight: 150, overflowY: 'auto', marginBottom: 8, lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      color: stone[700],
                    }}>
                      {msg.templateData.body
                        ? (msg.templateData.body.substring(0, 400) + (msg.templateData.body.length > 400 ? '...' : ''))
                        : '(No body content)'}
                    </div>
                  )}
                  <button
                    onClick={() => handleUseTemplate(msg.templateData)}
                    style={{
                      ...btnBase, padding: '8px 14px', width: '100%', justifyContent: 'center',
                      background: typeInfo?.color, color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 8,
                    }}
                  >
                    <Wand2 size={13} /> Use This Template
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: typeInfo?.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={12} color={typeInfo?.color} />
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 12, background: stone[50], fontSize: 12, color: stone[500], borderTopLeftRadius: 4 }}>
              <span className="ai-typing">Generating template</span>
              <style>{`.ai-typing::after { content: '...'; animation: dots 1.5s infinite; } @keyframes dots { 0%,20% { content: '.'; } 40% { content: '..'; } 60%,100% { content: '...'; } }`}</style>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (shown when no user messages yet) */}
      {messages.length <= 1 && (
        <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: stone[400], textTransform: 'uppercase', marginBottom: 2 }}>Suggestions</div>
          {(SUGGESTIONS[templateType] || []).map((s, i) => (
            <button key={i} onClick={() => sendMessage(s)}
              style={{
                ...btnBase, padding: '8px 12px', background: stone[50], color: stone[700],
                fontSize: 11, borderRadius: 8, textAlign: 'left', lineHeight: 1.4,
                border: `1px solid ${stone[200]}`, width: '100%',
              }}>
              <ArrowRight size={11} color={typeInfo?.color} style={{ flexShrink: 0 }} />
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: 12, borderTop: `1px solid ${stone[200]}`, background: stone[50] }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
            placeholder={`Describe the ${typeLabel.toLowerCase()} template you need...`}
            style={{ ...inputStyle, flex: 1, background: '#fff' }}
            disabled={loading}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            style={{
              ...btnBase, padding: '8px 14px',
              background: !input.trim() || loading ? stone[200] : typeInfo?.color,
              color: '#fff', fontSize: 12, fontWeight: 700,
            }}
          >
            <Send size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Template Editor Modal ─── */
function TemplateEditor({ template, templateType, onSave, onClose, readOnly }) {
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    subject: template?.subject || '',
    body: template?.body || '',
    category: template?.category || 'General',
    tags: template?.tags || '',
    variables: template?.variables || '[]',
    minRole: template?.minRole || 'Viewer',
  });
  const [bodyView, setBodyView] = useState('code'); // 'code' | 'preview' | 'split'
  const previewRef = useRef(null);

  const categories = CATEGORIES[templateType] || ['General'];
  const isEmail = templateType === 'email';
  const isPrompt = templateType === 'prompt';

  // Update iframe preview when body changes or view switches
  useEffect(() => {
    if (!isEmail || bodyView === 'code') return;
    const iframe = previewRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    // Replace ${variable} placeholders with styled badges for preview
    const previewHtml = (form.body || '').replace(
      /\$\{(\w+)\}/g,
      '<span style="background:#e0e7ff;color:#3730a3;padding:1px 6px;border-radius:4px;font-size:12px;font-family:monospace;">${$1}</span>'
    );
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;font-family:Inter,system-ui,sans-serif;font-size:14px;color:#1c1917;}</style></head><body>${previewHtml}</body></html>`);
    doc.close();
  }, [form.body, bodyView, isEmail]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${stone[200]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: stone[900] }}>
            {readOnly ? 'View' : template ? 'Edit' : 'New'} {TEMPLATE_TYPES.find(t => t.id === templateType)?.label?.replace('Templates', 'Template') || 'Template'}
          </h3>
          <button onClick={onClose} style={{ ...btnBase, padding: '6px 12px', background: stone[100], color: stone[600], fontSize: 12 }}>Close</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="Template name" disabled={readOnly} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle} disabled={readOnly}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Min Role</label>
              <select value={form.minRole} onChange={e => setForm({ ...form, minRole: e.target.value })} style={inputStyle} disabled={readOnly}>
                {['Viewer', 'Developer', 'Tenant Admin', 'Org Admin', 'Super Admin'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={inputStyle} placeholder="Brief description" disabled={readOnly} />
          </div>

          {isEmail && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Subject Line</label>
              <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} style={inputStyle} placeholder="Email subject with ${variables}" disabled={readOnly} />
            </div>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600] }}>
                {isEmail ? 'Email Body (HTML supported)' : isPrompt ? 'Prompt Template' : 'Template Body'}
              </label>
              {isEmail && (
                <div style={{ display: 'flex', gap: 0, border: `1px solid ${stone[200]}`, borderRadius: 6, overflow: 'hidden' }}>
                  {[
                    { id: 'code', label: 'Code', icon: '< >' },
                    { id: 'preview', label: 'Preview', icon: null },
                    { id: 'split', label: 'Split', icon: null },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setBodyView(tab.id)}
                      style={{
                        ...btnBase, borderRadius: 0, padding: '4px 12px', fontSize: 11, fontWeight: 600,
                        background: bodyView === tab.id ? BRAND.primary : '#fff',
                        color: bodyView === tab.id ? '#fff' : stone[500],
                        borderRight: tab.id !== 'split' ? `1px solid ${stone[200]}` : 'none',
                      }}
                    >
                      {tab.id === 'preview' && <Eye size={12} />}
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Non-email: always show textarea */}
            {!isEmail && (
              <textarea
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                style={{ ...inputStyle, minHeight: 250, fontFamily: isPrompt || templateType === 'workflow' ? 'monospace' : 'inherit', fontSize: 12, lineHeight: 1.6, resize: 'vertical' }}
                placeholder={isPrompt ? 'Analyze the following security event:\n\n${event_data}\n\nProvide: severity, classification, recommended actions.' : 'Template content...'}
                disabled={readOnly}
              />
            )}

            {/* Email — Code view */}
            {isEmail && (bodyView === 'code' || bodyView === 'split') && (
              <div style={{ display: bodyView === 'split' ? 'flex' : 'block', gap: 12 }}>
                <textarea
                  value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                  style={{
                    ...inputStyle,
                    minHeight: bodyView === 'split' ? 300 : 250,
                    fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, resize: 'vertical',
                    flex: bodyView === 'split' ? 1 : undefined,
                  }}
                  placeholder='<div style="font-family:Inter,sans-serif;max-width:600px;margin:0 auto">&#10;  <h2>${incident_title}</h2>&#10;  <p>A new incident has been created...</p>&#10;</div>'
                  disabled={readOnly}
                />
                {bodyView === 'split' && (
                  <div style={{ flex: 1, border: `1px solid ${stone[200]}`, borderRadius: 6, overflow: 'hidden', minHeight: 300 }}>
                    <div style={{ padding: '4px 10px', background: stone[50], borderBottom: `1px solid ${stone[200]}`, fontSize: 10, fontWeight: 600, color: stone[400], letterSpacing: 0.5 }}>
                      PREVIEW
                    </div>
                    <iframe
                      ref={previewRef}
                      title="Email Preview"
                      sandbox="allow-same-origin"
                      style={{ width: '100%', height: 'calc(100% - 26px)', border: 'none', background: '#fff' }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Email — Full preview */}
            {isEmail && bodyView === 'preview' && (
              <div style={{ border: `1px solid ${stone[200]}`, borderRadius: 6, overflow: 'hidden', minHeight: 300 }}>
                <div style={{ padding: '6px 12px', background: stone[50], borderBottom: `1px solid ${stone[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: stone[400], letterSpacing: 0.5 }}>HTML PREVIEW</span>
                  <span style={{ fontSize: 10, color: stone[400] }}>Variables shown as badges</span>
                </div>
                <iframe
                  ref={previewRef}
                  title="Email Preview"
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: 350, border: 'none', background: '#fff' }}
                />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} style={inputStyle} placeholder="incident, phishing, notification" disabled={readOnly} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Variables (JSON array)</label>
              <input value={form.variables} onChange={e => setForm({ ...form, variables: e.target.value })} style={inputStyle} placeholder='["incident_id", "severity"]' disabled={readOnly} />
            </div>
          </div>
        </div>

        {/* Footer */}
        {!readOnly && (
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${stone[200]}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={{ ...btnBase, padding: '8px 16px', background: stone[100], color: stone[600], fontSize: 12, fontWeight: 600 }}>Cancel</button>
            <button
              onClick={() => onSave({ ...form, templateType })}
              disabled={!form.name || !form.body}
              style={{ ...btnBase, padding: '8px 20px', background: !form.name || !form.body ? stone[200] : BRAND.primary, color: '#fff', fontSize: 12, fontWeight: 700 }}
            >
              {template ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Email Detail Modal ─── */
function EmailDetailModal({ email, onClose }) {
  if (!email) return null;
  const statusColor = email.status === 'sent' ? '#059669' : email.status === 'failed' ? '#ef4444' : '#d97706';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${stone[200]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: stone[900] }}>Email Details</h3>
            <Badge text={email.status?.toUpperCase()} color={statusColor} bg={statusColor + '15'} />
          </div>
          <button onClick={onClose} style={{ ...btnBase, padding: '6px 12px', background: stone[100], color: stone[600], fontSize: 12 }}>Close</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>TO</span><div style={{ fontSize: 13, color: stone[900] }}>{email.toAddress}</div></div>
            <div><span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>FROM</span><div style={{ fontSize: 13, color: stone[900] }}>{email.fromAddress || '—'}</div></div>
            <div><span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>WORKFLOW</span><div style={{ fontSize: 13, color: stone[900] }}>{email.workflowName || '—'}</div></div>
            <div><span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>SENT</span><div style={{ fontSize: 13, color: stone[900] }}>{email.sentAt ? new Date(email.sentAt).toLocaleString() : '—'}</div></div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>SUBJECT</span>
            <div style={{ fontSize: 14, fontWeight: 600, color: stone[900] }}>{email.subject || '(no subject)'}</div>
          </div>
          <div>
            <span style={{ fontSize: 10, color: stone[400], fontWeight: 600 }}>BODY</span>
            <div style={{ marginTop: 6, padding: 16, background: stone[50], border: `1px solid ${stone[200]}`, borderRadius: 8, fontSize: 13, lineHeight: 1.6, maxHeight: 300, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: email.body || '<em>No content</em>' }}
            />
          </div>
          {email.errorMessage && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <span style={{ fontSize: 10, color: '#991b1b', fontWeight: 600 }}>ERROR</span>
              <div style={{ fontSize: 12, color: '#991b1b', marginTop: 4 }}>{email.errorMessage}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Test Email Modal ─── */
function TestEmailModal({ template, onClose, onSend }) {
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to) return;
    setSending(true);
    await onSend({ to, subject: template?.subject || 'Test Email', body: template?.body || '', templateName: template?.name });
    setSending(false);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 440, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: stone[900] }}>Send Test Email</h3>
        <label style={{ fontSize: 11, fontWeight: 600, color: stone[600], marginBottom: 4, display: 'block' }}>Recipient Email *</label>
        <input value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} placeholder="test@example.com" type="email" />
        <div style={{ fontSize: 11, color: stone[400], marginBottom: 16 }}>
          Template: <strong>{template?.name || 'None'}</strong> — Subject: <strong>{template?.subject || '(none)'}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ ...btnBase, padding: '8px 16px', background: stone[100], color: stone[600], fontSize: 12 }}>Cancel</button>
          <button onClick={handleSend} disabled={!to || sending} style={{ ...btnBase, padding: '8px 20px', background: !to ? stone[200] : BRAND.primary, color: '#fff', fontSize: 12, fontWeight: 700 }}>
            <Send size={13} /> {sending ? 'Sending...' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function TemplateCenterPage() {
  const { currentRole, isSuperAdmin, hasPermission } = useAuth();
  const { addToast } = useToast();

  // RBAC helpers
  const canCreate = currentRole === 'Super Admin' || currentRole === 'Org Admin' || currentRole === 'Tenant Admin';
  const canPublish = currentRole === 'Super Admin';
  const canDelete = currentRole === 'Super Admin';
  const isReadOnly = !canCreate;

  // State
  const [activeTab, setActiveTab] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);

  // AI Chat state
  const [aiChatOpen, setAiChatOpen] = useState(false);

  // Email log state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [testEmailModal, setTestEmailModal] = useState(null);
  const [emailTimeRange, setEmailTimeRange] = useState(168);

  // Load ALL templates (no type filter) so tab badges always show correct counts
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    const res = await getTemplates();
    if (res?.ok) setTemplates(res.data || []);
    setLoading(false);
  }, []);

  // Load email logs
  const loadEmailLogs = useCallback(async () => {
    const [logsRes, statsRes] = await Promise.all([
      getEmailLogs(emailTimeRange),
      getEmailStats(emailTimeRange),
    ]);
    if (logsRes?.ok) setEmailLogs(logsRes.data || []);
    if (statsRes?.ok) setEmailStats(statsRes.data || null);
  }, [emailTimeRange]);

  // Load all templates on mount
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Load email logs when email tab is active
  useEffect(() => {
    if (activeTab === 'emails') loadEmailLogs();
  }, [activeTab, loadEmailLogs]);

  // Handlers
  const handleSave = async (formData) => {
    let res;
    if (editingTemplate) {
      res = await updateTemplate(editingTemplate.id, formData);
    } else {
      res = await createTemplate(formData);
    }
    if (res?.ok) {
      addToast(editingTemplate ? 'Template updated' : 'Template created', 'success');
      setEditorOpen(false);
      setEditingTemplate(null);
      loadTemplates();
    } else {
      addToast(res?.error || 'Failed to save template', 'error');
    }
  };

  const handlePublish = async (id) => {
    const res = await publishTemplate(id);
    if (res?.ok) { addToast('Template published', 'success'); loadTemplates(); }
    else addToast('Failed to publish', 'error');
  };

  const handleArchive = async (id) => {
    const res = await archiveTemplate(id);
    if (res?.ok) { addToast('Template archived', 'success'); loadTemplates(); }
    else addToast('Failed to archive', 'error');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template permanently?')) return;
    const res = await deleteTemplate(id);
    if (res?.ok) { addToast('Template deleted', 'success'); loadTemplates(); }
    else addToast('Failed to delete', 'error');
  };

  const handleDuplicate = async (id) => {
    const res = await duplicateTemplate(id);
    if (res?.ok) { addToast('Template duplicated', 'success'); loadTemplates(); }
    else addToast('Failed to duplicate', 'error');
  };

  const handleTestEmail = async (payload) => {
    const res = await sendTemplateTestEmail(payload);
    if (res?.ok) { addToast('Test email sent', 'success'); if (activeTab === 'emails') loadEmailLogs(); }
    else addToast(res?.error || 'Failed to send test', 'error');
  };

  // AI Chat: insert generated template into editor
  const handleAIInsertTemplate = async (templateData) => {
    const payload = {
      templateType: activeTab,
      name: templateData.name || 'AI Generated Template',
      description: templateData.description || '',
      subject: templateData.subject || '',
      body: templateData.body || '',
      category: templateData.category || 'General',
      tags: templateData.tags || '',
      variables: templateData.variables || '[]',
      minRole: 'Viewer',
    };
    const res = await createTemplate(payload);
    if (res?.ok) {
      addToast('AI template saved to Template Center', 'success');
      loadTemplates();
    } else {
      addToast(res?.error || 'Failed to save AI template', 'error');
    }
  };

  // Filter templates — first by active tab type, then by search/status/category
  const filtered = templates.filter(t => {
    if (t.templateType !== activeTab) return false;
    if (searchTerm && !t.name?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    return true;
  });

  const tabs = [
    ...TEMPLATE_TYPES.map(t => ({ id: t.id, label: t.label, icon: t.icon, color: t.color })),
    { id: 'emails', label: 'Email Log', icon: BarChart3, color: '#0891b2' },
  ];

  return (
    <V2PageShell
      title="Template Center"
      subtitle="Reusable agent / workflow / skill templates."
      tags={<V2Pill tone="cyan" size="sm">Build</V2Pill>}
      actions={
        canCreate && activeTab !== 'emails' ? (
          <>
            <V2Button
              variant={aiChatOpen ? 'secondary' : 'primary'}
              size="sm"
              iconLeft={<Sparkles size={14} />}
              onClick={() => setAiChatOpen(!aiChatOpen)}
            >
              {aiChatOpen ? 'Close AI Builder' : 'AI Builder'}
            </V2Button>
            <V2Button
              variant="primary"
              size="sm"
              iconLeft={<Plus size={14} />}
              onClick={() => { setEditingTemplate(null); setViewOnly(false); setEditorOpen(true); }}
            >
              New Template
            </V2Button>
          </>
        ) : null
      }
    >
      {/* G94 — V2PageShell owns the page chrome; no extra wrapping divs needed. */}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginTop: 16, borderBottom: `1px solid ${stone[200]}` }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); }}
                style={{ ...btnBase, padding: '10px 16px', background: 'transparent', color: active ? tab.color : stone[500], borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent', fontSize: 12, fontWeight: active ? 700 : 500, borderRadius: 0, marginBottom: -1 }}>
                <Icon size={14} /> {tab.label}
                {tab.id !== 'emails' && (
                  <span style={{ fontSize: 10, background: active ? tab.color + '15' : stone[100], color: active ? tab.color : stone[400], padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>
                    {templates.filter(t => t.templateType === tab.id).length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, marginRight: aiChatOpen ? 380 : 0, transition: 'margin-right 0.2s ease' }}>
        {activeTab === 'emails' ? (
          /* ═══ EMAIL LOG TAB ═══ */
          <div>
            {/* Email Stats */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <StatCard label="Total Emails" value={emailStats?.total ?? 0} icon={Mail} />
              <StatCard label="Sent" value={emailStats?.sent ?? 0} icon={CheckCircle} color="#059669" />
              <StatCard label="Failed" value={emailStats?.failed ?? 0} icon={XCircle} color="#ef4444" />
              <StatCard label="Success Rate" value={`${(emailStats?.successRate ?? 0).toFixed(1)}%`} icon={Zap} color="#2563eb" />
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: stone[400] }} />
                <input placeholder="Search emails..." style={{ ...inputStyle, paddingLeft: 32 }}
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <select value={emailTimeRange} onChange={e => setEmailTimeRange(Number(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
                <option value={24}>Last 24h</option>
                <option value={72}>Last 3d</option>
                <option value={168}>Last 7d</option>
                <option value={720}>Last 30d</option>
              </select>
              <button onClick={loadEmailLogs} style={{ ...btnBase, padding: '8px 12px', background: stone[100], color: stone[600], fontSize: 12 }}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {/* Email Log Table */}
            <div style={{ background: '#fff', border: `1px solid ${stone[200]}`, borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: stone[50], borderBottom: `1px solid ${stone[200]}` }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>To</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>Subject</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>Workflow</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>Sent</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: stone[500], fontSize: 10, textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.filter(e => !searchTerm || e.toAddress?.toLowerCase().includes(searchTerm.toLowerCase()) || e.subject?.toLowerCase().includes(searchTerm.toLowerCase())).map(email => {
                    const sc = email.status === 'sent' ? '#059669' : email.status === 'failed' ? '#ef4444' : '#d97706';
                    return (
                      <tr key={email.id} style={{ borderBottom: `1px solid ${stone[100]}`, cursor: 'pointer' }} onClick={() => setEmailDetail(email)}>
                        <td style={{ padding: '10px 14px' }}><Badge text={email.status} color={sc} bg={sc + '15'} /></td>
                        <td style={{ padding: '10px 14px', color: stone[900], fontWeight: 500 }}>{email.toAddress}</td>
                        <td style={{ padding: '10px 14px', color: stone[700] }}>{email.subject || '—'}</td>
                        <td style={{ padding: '10px 14px', color: stone[500] }}>{email.workflowName || '—'}</td>
                        <td style={{ padding: '10px 14px', color: stone[400], fontSize: 11 }}>{email.sentAt ? new Date(email.sentAt).toLocaleString() : '—'}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          <button onClick={e => { e.stopPropagation(); setEmailDetail(email); }} style={{ ...btnBase, padding: '4px 8px', background: stone[100], color: stone[600], fontSize: 10 }}>
                            <Eye size={11} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {emailLogs.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: stone[400], fontSize: 13 }}>No emails found in this time range</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* ═══ TEMPLATE TABS ═══ */
          <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: stone[400] }} />
                <input placeholder="Search templates..." style={{ ...inputStyle, paddingLeft: 32 }}
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                <option value="all">All Categories</option>
                {(CATEGORIES[activeTab] || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={loadTemplates} style={{ ...btnBase, padding: '8px 12px', background: stone[100], color: stone[600], fontSize: 12 }}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {/* Template Grid */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: stone[400] }}>Loading templates...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: stone[400] }}>
                <FileText size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No {TEMPLATE_TYPES.find(t => t.id === activeTab)?.label?.toLowerCase() || 'templates'} found</div>
                <div style={{ fontSize: 12, marginBottom: 16 }}>
                  {canCreate ? 'Create your first template to get started.' : 'No templates available for your role.'}
                </div>
                {canCreate && (
                  <button onClick={() => setAiChatOpen(true)}
                    style={{ ...btnBase, padding: '10px 20px', background: `linear-gradient(135deg, ${TEMPLATE_TYPES.find(t => t.id === activeTab)?.color || BRAND.primary}, ${BRAND.primary})`, color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 8 }}>
                    <Sparkles size={14} /> Generate with AI Builder
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                {filtered.map(template => {
                  const typeInfo = TEMPLATE_TYPES.find(t => t.id === template.templateType);
                  const TypeIcon = typeInfo?.icon || FileText;
                  const statusBg = template.status === 'published' ? '#ecfdf5' : template.status === 'archived' ? '#f5f5f4' : '#fffbeb';
                  const statusColor = template.status === 'published' ? '#059669' : template.status === 'archived' ? stone[500] : '#d97706';

                  return (
                    <div key={template.id} style={{ background: '#fff', border: `1px solid ${stone[200]}`, borderRadius: 8, padding: 16, display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow 0.15s', cursor: 'pointer' }}
                      onClick={() => { setEditingTemplate(template); setViewOnly(isReadOnly); setEditorOpen(true); }}>
                      {/* Top row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TypeIcon size={16} color={typeInfo?.color} />
                          <span style={{ fontSize: 14, fontWeight: 700, color: stone[900] }}>{template.name}</span>
                        </div>
                        <Badge text={template.status} color={statusColor} bg={statusBg} />
                      </div>

                      {/* Description */}
                      <div style={{ fontSize: 12, color: stone[500], lineHeight: 1.5, minHeight: 36 }}>
                        {template.description || 'No description'}
                      </div>

                      {/* Meta */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {template.category && <Badge text={template.category} />}
                        <Badge text={`v${template.version}`} />
                        {template.minRole && template.minRole !== 'Viewer' && <Badge text={`🔒 ${template.minRole}+`} />}
                      </div>

                      {/* Footer */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, paddingTop: 8, borderTop: `1px solid ${stone[100]}` }}>
                        <span style={{ fontSize: 10, color: stone[400] }}>
                          {template.createdByName || 'System'} — {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString() : ''}
                        </span>
                        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                          {activeTab === 'email' && (
                            <button onClick={() => setTestEmailModal(template)} title="Send test email"
                              style={{ ...btnBase, padding: '4px 6px', background: stone[50], color: stone[500], fontSize: 10 }}>
                              <Send size={11} />
                            </button>
                          )}
                          {canCreate && (
                            <button onClick={() => { setEditingTemplate(template); setViewOnly(false); setEditorOpen(true); }} title="Edit"
                              style={{ ...btnBase, padding: '4px 6px', background: stone[50], color: stone[500], fontSize: 10 }}>
                              <Edit3 size={11} />
                            </button>
                          )}
                          <button onClick={() => handleDuplicate(template.id)} title="Duplicate"
                            style={{ ...btnBase, padding: '4px 6px', background: stone[50], color: stone[500], fontSize: 10 }}>
                            <Copy size={11} />
                          </button>
                          {canPublish && template.status === 'draft' && (
                            <button onClick={() => handlePublish(template.id)} title="Publish"
                              style={{ ...btnBase, padding: '4px 6px', background: '#ecfdf5', color: '#059669', fontSize: 10 }}>
                              <CheckCircle size={11} />
                            </button>
                          )}
                          {canPublish && template.status === 'published' && (
                            <button onClick={() => handleArchive(template.id)} title="Archive"
                              style={{ ...btnBase, padding: '4px 6px', background: '#fef2f2', color: '#d97706', fontSize: 10 }}>
                              <Archive size={11} />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(template.id)} title="Delete"
                              style={{ ...btnBase, padding: '4px 6px', background: '#fef2f2', color: '#ef4444', fontSize: 10 }}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Chat Panel */}
      {aiChatOpen && activeTab !== 'emails' && (
        <AITemplateChatPanel
          templateType={activeTab}
          onInsertTemplate={handleAIInsertTemplate}
          onClose={() => setAiChatOpen(false)}
        />
      )}

      {/* Modals */}
      {editorOpen && (
        <TemplateEditor
          template={editingTemplate}
          templateType={activeTab}
          onSave={handleSave}
          onClose={() => { setEditorOpen(false); setEditingTemplate(null); }}
          readOnly={viewOnly}
        />
      )}
      {emailDetail && <EmailDetailModal email={emailDetail} onClose={() => setEmailDetail(null)} />}
      {testEmailModal && <TestEmailModal template={testEmailModal} onClose={() => setTestEmailModal(null)} onSend={handleTestEmail} />}
    </V2PageShell>
  );
}
